import { BlobServiceClient, ContainerClient } from '@azure/storage-blob';
import { useEffect, useState } from 'react';
import { handleError } from '~/utils/errors';
import useBlobServiceClient from './useBlobService';

export type BlobNode = {
    name: string;
    fullPath: string;
    isDirectory: boolean;
    children: BlobNode[];
    container: string;
};

// Recursively adds blobs to the hierarchy, representing the file structure
const addBlobToHierarchy = async (
    containerClient: ContainerClient,
    containerName: string,
    current: BlobNode,
    prefix: string
) => {
    // List blobs using the specified prefix to simulate a directory structure
    const blobs = containerClient.listBlobsByHierarchy('/', { prefix });
    for await (const blob of blobs) {
        // Extract the relevant part of the blob's path and determine if it's a directory
        const fullPath = blob.name;
        const name = fullPath.slice(prefix.length).split('/')[0];
        const isDirectory = blob.kind === 'prefix';

        // Find or create the child node in the current directory structure
        let child = current.children.find((c) => c.name === name);
        if (!child) {
            child = {
                name: name,
                fullPath: fullPath,
                isDirectory: isDirectory,
                children: [],
                container: containerName,
            };
            current.children.push(child);
        }

        // If the child is a directory, recursively add its children
        if (child.isDirectory) {
            await addBlobToHierarchy(
                containerClient,
                containerName,
                child,
                fullPath
            );
        }
    }
};

// Lists all blobs in a container and constructs a hierarchical structure
async function listBlobsInContainer(
    blobServiceClient: BlobServiceClient,
    containerName: string
): Promise<BlobNode> {
    const containerClient = blobServiceClient.getContainerClient(containerName);
    const root: BlobNode = {
        name: containerName,
        fullPath: '',
        isDirectory: true,
        children: [],
        container: containerName,
    };

    // Build the hierarchical structure starting from the root
    await addBlobToHierarchy(containerClient, containerName, root, '');

    return root;
}

/**
 * A React hook for managing Azure Blob Containers and their contents.
 *
 * This hook uses Azure Blob Storage SDK to list all containers in a given storage account and
 * construct a hierarchical structure of the blobs within each container. It also provides functionality
 * to download a specific blob. The hierarchical structure represents each blob as a node, which can be
 * either a file or a directory.
 *
 * @param {string} storageAccount - The name of the Azure Storage Account.
 * @returns An object containing:
 *   - `blobContainers`: An array of BlobNode representing the hierarchical structure of blobs in each container.
 *   - `loading`: A boolean indicating whether the blob data is currently being loaded.
 *   - `error`: An error object if an error occurred during the loading process, otherwise undefined.
 *   - `downloadBlob`: A function that can be called to download a specific blob.
 *
 */
const useAzureBlobContainers = (storageAccount: string) => {
    const [blobContainers, setBlobContainers] = useState<BlobNode[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<unknown>();
    const blobServiceClient = useBlobServiceClient(storageAccount);

    const downloadBlob = async (
        containerName: string,
        blobName: string
    ): Promise<Blob | undefined> => {
        if (!blobServiceClient) {
            return;
        }
        const containerClient =
            blobServiceClient.getContainerClient(containerName);
        const blobClient = containerClient.getBlobClient(blobName);

        try {
            const downloadBlockBlobResponse = await blobClient.download();
            const blob = await downloadBlockBlobResponse.blobBody;
            return blob;
        } catch (error) {
            handleError(error, 'Error when trying to fetch the blob.');
        }
    };

    // Fetch and structure the blob container data when the component mounts or when dependencies change
    useEffect(() => {
        const fetchBlobs = async () => {
            setLoading(true);
            if (!blobServiceClient) {
                setLoading(false);
                return;
            }
            try {
                const nodes = [];
                const containers = blobServiceClient.listContainers();
                for await (const containerItem of containers) {
                    const hierarchy = await listBlobsInContainer(
                        blobServiceClient,
                        containerItem.name
                    );
                    nodes.push(hierarchy);
                }
                setBlobContainers(nodes);
                setLoading(false);
            } catch (error) {
                setError(error);
                setLoading(false);
                handleError(
                    error,
                    'Error when trying to fetch the blob container.'
                );
            }
        };
        fetchBlobs();
    }, [blobServiceClient]);

    return { blobContainers, loading, error, downloadBlob };
};

export default useAzureBlobContainers;
